import { create } from 'zustand';
import apiClient from '../lib/api';

export interface BrandColorPreset {
  id: string;
  name: string;
  primary: string;
  secondary: string;
  accent?: string;
  description: string;
}

export const BRAND_COLOR_PRESETS: BrandColorPreset[] = [
  {
    id: 'slate-blue',
    name: 'Slate and Blue',
    primary: '#2563EB',
    secondary: '#F1F5F9',
    accent: '#EFF6FF',
    description: 'TeamOne v4 canonical calm slate and enterprise blue',
  },
  {
    id: 'ibm-blue',
    name: 'IBM Carbon Blue',
    primary: '#0F62FE',
    secondary: '#393939',
    accent: '#1192E8',
    description: 'Enterprise canonical IBM Carbon digital blue',
  },
  {
    id: 'royal-indigo',
    name: 'Modern Indigo',
    primary: '#4F46E5',
    secondary: '#7C3AED',
    accent: '#6366F1',
    description: 'Clean SaaS indigo with modern energetic accents',
  },
  {
    id: 'emerald-it',
    name: 'Emerald Infrastructure',
    primary: '#10B981',
    secondary: '#047857',
    accent: '#34D399',
    description: 'High-availability IT infrastructure & asset green',
  },
  {
    id: 'cyber-violet',
    name: 'Cyber Violet',
    primary: '#8B5CF6',
    secondary: '#6D28D9',
    accent: '#A78BFA',
    description: 'Tech-forward violet with creative digital presence',
  },
  {
    id: 'ocean-teal',
    name: 'Ocean Teal',
    primary: '#0EA5E9',
    secondary: '#0284C7',
    accent: '#38BDF8',
    description: 'Professional calm oceanic sky blue',
  },
  {
    id: 'sunset-amber',
    name: 'Industrial Amber',
    primary: '#D97706',
    secondary: '#B45309',
    accent: '#F59E0B',
    description: 'High-visibility industrial & equipment branding',
  },
  {
    id: 'crimson-ops',
    name: 'Crimson Operations',
    primary: '#E11D48',
    secondary: '#BE123C',
    accent: '#FB7185',
    description: 'Urgent mission-critical command operations',
  },
  {
    id: 'slate-pro',
    name: 'Monochrome Slate',
    primary: '#1E293B',
    secondary: '#475569',
    accent: '#64748B',
    description: 'Quiet architectural neutral graphite',
  },
];

export interface BrandingState {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logoUrl: string;
  faviconUrl: string;
  appName: string;
  tagline: string;
  activePresetId: string;

  setPrimaryColor: (color: string) => void;
  setSecondaryColor: (color: string) => void;
  setAccentColor: (color: string) => void;
  setLogoUrl: (url: string) => void;
  setFaviconUrl: (url: string) => void;
  setAppName: (name: string) => void;
  setTagline: (tagline: string) => void;
  applyPreset: (presetId: string) => void;
  applyBranding: (branding: Partial<{ primaryColor: string; secondaryColor: string; accentColor: string; logoUrl: string; faviconUrl: string; appName: string; tagline: string; activePresetId: string }>) => void;
  resetBranding: () => void;
  initBranding: () => void;
  // DB-backed persistence (public.tenants.settings->'branding')
  loadBrandingFromServer: () => Promise<void>;
  saveBrandingToServer: () => Promise<void>;
}

// The branding fields persisted to / read from the server.
const BRANDING_KEYS: (keyof BrandingState)[] = [
  'primaryColor', 'secondaryColor', 'accentColor',
  'logoUrl', 'faviconUrl', 'appName', 'tagline', 'activePresetId',
];

// Convert Hex to HSL values
export function hexToHsl(hex: string): { h: number; s: number; l: number; hslString: string } {
  let cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    cleaned = cleaned.split('').map((c) => c + c).join('');
  }
  if (cleaned.length !== 6) {
    return { h: 221, s: 83, l: 53, hslString: '221 83% 53%' };
  }

  const r = parseInt(cleaned.substring(0, 2), 16) / 255;
  const g = parseInt(cleaned.substring(2, 4), 16) / 255;
  const b = parseInt(cleaned.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  const hDeg = Math.round(h * 360);
  const sPct = Math.round(s * 100);
  const lPct = Math.round(l * 100);

  return {
    h: hDeg,
    s: sPct,
    l: lPct,
    hslString: `${hDeg} ${sPct}% ${lPct}%`,
  };
}

// Apply colors directly into root CSS Variables
export function applyThemeToDOM(primaryHex: string, secondaryHex?: string, accentHex?: string) {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const primaryHsl = hexToHsl(primaryHex);
  const secHsl = secondaryHex ? hexToHsl(secondaryHex) : hexToHsl('#393939');
  const accHsl = accentHex ? hexToHsl(accentHex) : primaryHsl;

  // Set HSL tokens (used by Tailwind primary / secondary / ring / accent)
  root.style.setProperty('--primary', primaryHsl.hslString);
  root.style.setProperty('--primary-end', primaryHsl.hslString);
  root.style.setProperty('--ring', primaryHsl.hslString);
  root.style.setProperty('--sidebar-active', primaryHsl.hslString);
  
  // Foreground contrast logic
  root.style.setProperty('--primary-foreground', primaryHsl.l > 65 ? '222 22% 12%' : '0 0% 100%');

  // Accent & highlight tokens
  root.style.setProperty('--accent', `${primaryHsl.h} ${Math.min(60, primaryHsl.s)}% 96%`);
  root.style.setProperty('--accent-foreground', `${primaryHsl.h} ${primaryHsl.s}% 40%`);

  // Explicit Hex variables for direct style references
  root.style.setProperty('--brand-primary', primaryHex);
  root.style.setProperty('--brand-secondary', secondaryHex || '#393939');
  root.style.setProperty('--brand-accent', accentHex || primaryHex);
  root.style.setProperty('--brand-primary-light', `${primaryHex}1a`);
  root.style.setProperty('--brand-primary-subtle', `${primaryHex}0d`);
}

// Fallback favicon shipped with the app (public/favicon.png). Used when the
// organization has not configured a custom one, or after a reset.
const DEFAULT_FAVICON = '/favicon.png';

// Point the browser tab icon at a configurable favicon. An empty value
// restores the shipped default. Updates every <link rel="icon"> in the head so
// the tab, bookmarks and PWA all stay consistent.
export function applyFaviconToDOM(url?: string) {
  if (typeof document === 'undefined') return;
  const href = url && url.trim() ? url.trim() : DEFAULT_FAVICON;
  const links = document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']");
  if (links.length === 0) {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    document.head.appendChild(link);
    return;
  }
  links.forEach((link) => {
    link.href = href;
  });
}

// Default application / brand name shown in the chrome (title bar, sidebar,
// dashboard, logo wordmark). Overridable per organization.
const DEFAULT_APP_NAME = 'TeamOne';

// Default tagline shown under the logo wordmark.
const DEFAULT_TAGLINE = 'Business Platform';

// Reflect the configurable app name into the browser tab title.
export function applyAppNameToDOM(name?: string) {
  if (typeof document === 'undefined') return;
  const value = name && name.trim() ? name.trim() : DEFAULT_APP_NAME;
  document.title = value;
}

const STORAGE_KEY = 'teamone_organization_branding';

const DEFAULT_BRANDING = {
  primaryColor: '#2563EB',
  secondaryColor: '#F1F5F9',
  accentColor: '#EFF6FF',
  logoUrl: '',
  faviconUrl: '',
  appName: DEFAULT_APP_NAME,
  tagline: DEFAULT_TAGLINE,
  activePresetId: 'ibm-blue',
};

function loadSavedBranding() {
  if (typeof window === 'undefined') return DEFAULT_BRANDING;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_BRANDING, ...parsed };
    }
  } catch (e) {
    console.error('Failed to parse saved branding from localStorage', e);
  }
  return DEFAULT_BRANDING;
}

export const useBrandingStore = create<BrandingState>((set, get) => {
  const initial = loadSavedBranding();
  if (typeof window !== 'undefined') {
    applyThemeToDOM(initial.primaryColor, initial.secondaryColor, initial.accentColor);
    applyFaviconToDOM(initial.faviconUrl);
    applyAppNameToDOM(initial.appName);
  }

  return {
    ...initial,

    setPrimaryColor: (color: string) => {
      set({ primaryColor: color, activePresetId: 'custom' });
      applyThemeToDOM(color, get().secondaryColor, get().accentColor);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...get(), primaryColor: color, activePresetId: 'custom' }));
      } catch (e) {}
    },

    setSecondaryColor: (color: string) => {
      set({ secondaryColor: color, activePresetId: 'custom' });
      applyThemeToDOM(get().primaryColor, color, get().accentColor);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...get(), secondaryColor: color, activePresetId: 'custom' }));
      } catch (e) {}
    },

    setAccentColor: (color: string) => {
      set({ accentColor: color, activePresetId: 'custom' });
      applyThemeToDOM(get().primaryColor, get().secondaryColor, color);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...get(), accentColor: color, activePresetId: 'custom' }));
      } catch (e) {}
    },

    setLogoUrl: (url: string) => {
      set({ logoUrl: url });
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...get(), logoUrl: url }));
      } catch (e) {}
    },

    setFaviconUrl: (url: string) => {
      set({ faviconUrl: url });
      applyFaviconToDOM(url);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...get(), faviconUrl: url }));
      } catch (e) {}
    },

    setAppName: (name: string) => {
      set({ appName: name });
      applyAppNameToDOM(name);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...get(), appName: name }));
      } catch (e) {}
    },

    setTagline: (tagline: string) => {
      set({ tagline });
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...get(), tagline }));
      } catch (e) {}
    },

    applyPreset: (presetId: string) => {
      const preset = BRAND_COLOR_PRESETS.find((p) => p.id === presetId);
      if (!preset) return;
      const updated = {
        primaryColor: preset.primary,
        secondaryColor: preset.secondary,
        accentColor: preset.accent || preset.primary,
        activePresetId: preset.id,
      };
      set(updated);
      applyThemeToDOM(updated.primaryColor, updated.secondaryColor, updated.accentColor);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...get(), ...updated }));
      } catch (e) {}
    },

    applyBranding: (partial) => {
      const updated = { ...get(), ...partial };
      set(updated);
      applyThemeToDOM(updated.primaryColor, updated.secondaryColor, updated.accentColor);
      applyFaviconToDOM(updated.faviconUrl);
      applyAppNameToDOM(updated.appName);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {}
    },

    resetBranding: () => {
      set(DEFAULT_BRANDING);
      applyThemeToDOM(DEFAULT_BRANDING.primaryColor, DEFAULT_BRANDING.secondaryColor, DEFAULT_BRANDING.accentColor);
      applyFaviconToDOM(DEFAULT_BRANDING.faviconUrl);
      applyAppNameToDOM(DEFAULT_BRANDING.appName);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
    },

    initBranding: () => {
      const current = loadSavedBranding();
      set(current);
      applyThemeToDOM(current.primaryColor, current.secondaryColor, current.accentColor);
      applyFaviconToDOM(current.faviconUrl);
      applyAppNameToDOM(current.appName);
    },

    // Pull branding saved in the database (public.tenants.settings->'branding')
    // and apply it app-wide. Called after sign-in so every device/browser shows
    // the organization's configured brand, not just whatever this browser cached.
    loadBrandingFromServer: async () => {
      try {
        const res = await apiClient
          .get('/admin/organization')
          .catch(() => apiClient.get('/auth/admin/organization'));
        const org = res.data?.data ?? res.data;
        const branding = org?.settings?.branding;
        if (branding && typeof branding === 'object' && Object.keys(branding).length > 0) {
          get().applyBranding(branding);
        }
      } catch (e) {
        // Non-fatal: fall back to the locally cached branding.
        console.error('Failed to load branding from server', e);
      }
    },

    // Persist the current branding to the database so it survives across devices.
    saveBrandingToServer: async () => {
      const state = get();
      const branding: Record<string, string> = {};
      BRANDING_KEYS.forEach((k) => {
        branding[k] = String(state[k as keyof BrandingState] ?? '');
      });
      await apiClient
        .patch('/admin/organization', { branding })
        .catch(() => apiClient.patch('/auth/admin/organization', { branding }));
    },
  };
});
